import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useNativePlatform } from "@/hooks/useNativePlatform"
import { buttonVariants } from "@/components/ui/button"
import {
  nativeSheetBottomPad,
  nativeSheetScrollBottomPad,
} from "@/lib/native/nativeSheetPadding"
import { nativeOverlayZ, nativeSheetZ } from "@/lib/native/nativeZIndex"
import { useNativeSheetSwipe } from "@/lib/native/useNativeSheetSwipe"
import {
  NativeSheetDismissProvider,
  useNativeSheetDismissContext,
} from "@/lib/native/nativeSheetDismissContext"
import { bindNativeAwareOpenChange } from "@/lib/native/nativeOpenChange"
import { isNativeParentSheetOpen } from "@/lib/native/nativeNestedParent"
import { useNativeSheetEnterStabilizer } from "@/lib/native/useNativeSheetEnterStabilizer"

const nativeSheetLayoutLock =
  "!inset-x-0 !left-0 !right-0 !w-full !max-w-none !mx-0"

const AlertDialog = ({
  onOpenChange,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Root>) => {
  const finishClose = React.useCallback(() => {
    onOpenChange?.(false)
  }, [onOpenChange])

  const handleOpenChange = React.useCallback(
    bindNativeAwareOpenChange(onOpenChange),
    [onOpenChange],
  )

  return (
    <NativeSheetDismissProvider onRequestClose={finishClose}>
      <AlertDialogPrimitive.Root onOpenChange={handleOpenChange} {...props} />
    </NativeSheetDismissProvider>
  )
}

const AlertDialogTrigger = AlertDialogPrimitive.Trigger

const AlertDialogPortal = AlertDialogPrimitive.Portal

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const { isNative } = useNativePlatform()
  return (
    <AlertDialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 h-dvh w-dvh",
        isNative
          ? `${nativeOverlayZ} bg-black/55`
          : "z-[220] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
      ref={ref}
    />
  )
})
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName

interface AlertDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> {
  /** Native bottom sheet: show top-right close (X). Default true. */
  showCloseButton?: boolean
  /** Stack above an open native sheet (e.g. confirm inside CreateTest dialog). */
  nested?: boolean
  /** Nested sheet without a second dimmed backdrop (parent stays visible). */
  nestedUndimmed?: boolean
  sheetOpen?: boolean
}

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(({ className, children, showCloseButton = true, nested = false, nestedUndimmed = false, sheetOpen = true, ...props }, ref) => {
  const { isNative } = useNativePlatform()
  const dismissCtx = useNativeSheetDismissContext()
  const enterStable = useNativeSheetEnterStabilizer(Boolean(sheetOpen))
  const sheetSwipe = useNativeSheetSwipe({ enabled: isNative })
  const parentSheetOpen = isNative && isNativeParentSheetOpen()
  const effectiveNested = nested || parentSheetOpen
  const overlayZ = effectiveNested ? "z-[230]" : nativeOverlayZ
  const sheetZ = effectiveNested ? "z-[231]" : nativeSheetZ
  const nestedOverlayClass =
    effectiveNested && nestedUndimmed
      ? "!bg-transparent !backdrop-blur-none pointer-events-none"
      : effectiveNested
        ? "bg-black/55"
        : undefined

  const webDismissHandlers = {
    onPointerDownOutside: (event: Event) => event.preventDefault(),
    onInteractOutside: (event: Event) => event.preventDefault(),
  }

  if (isNative) {
    const overlayDismiss =
      !nestedUndimmed ? { onPointerDown: sheetSwipe.backdropPointerDown } : {}
    return (
      <AlertDialogPortal>
        <AlertDialogOverlay
          className={cn(overlayZ, nestedOverlayClass, enterStable, "native-overlay-stable")}
          {...overlayDismiss}
        />
        <AlertDialogPrimitive.Content
          ref={sheetSwipe.mergeSheetRef(ref)}
          className={cn(
            `fixed inset-x-0 bottom-0 top-auto ${sheetZ} w-full max-w-none mx-0`,
            effectiveNested && "native-nested-sheet",
            effectiveNested && nestedUndimmed && "native-nested-undimmed",
            "native-bottom-sheet flex flex-col gap-0 border bg-background p-0 shadow-lg",
            "rounded-t-2xl rounded-b-none overflow-hidden",
            enterStable,
            "max-h-[70dvh] h-auto pointer-events-auto will-change-transform",
            nativeSheetBottomPad,
            nativeSheetLayoutLock,
            className,
          )}
          onPointerDownOutside={sheetSwipe.blockRadixDismiss}
          onInteractOutside={sheetSwipe.blockRadixDismiss}
          onEscapeKeyDown={(e) => {
            e.preventDefault()
            dismissCtx?.requestClose()
          }}
          {...props}
        >
          {sheetSwipe.dragHandle}
          <div
            className={cn(
              "native-sheet-body overflow-y-auto overflow-x-hidden flex-1 min-h-0 overscroll-contain px-4 pb-4",
              nativeSheetScrollBottomPad,
            )}
          >
            {children}
          </div>
          {showCloseButton && (
            <button
              type="button"
              className="absolute right-3 top-3 rounded-full opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring z-[101] h-9 w-9 flex items-center justify-center bg-background/90 backdrop-blur-sm border border-border shadow-sm"
              aria-label="Close"
              onClick={() => dismissCtx?.requestClose()}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </AlertDialogPrimitive.Content>
      </AlertDialogPortal>
    )
  }

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-[221] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-xl sm:rounded-2xl w-[calc(100%-4px)] max-h-[85dvh] overflow-y-auto",
          className,
        )}
        {...webDismissHandlers}
        {...props}
      >
        {children}
      </AlertDialogPrimitive.Content>
    </AlertDialogPortal>
  )
})
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
AlertDialogHeader.displayName = "AlertDialogHeader"

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
AlertDialogFooter.displayName = "AlertDialogFooter"

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold", className)}
    {...props}
  />
))
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, onClick, ...props }, ref) => {
  const dismissCtx = useNativeSheetDismissContext()
  const { isNative } = useNativePlatform()
  return (
    <AlertDialogPrimitive.Action
      ref={ref}
      className={cn(buttonVariants(), className)}
      onClick={(e) => {
        onClick?.(e)
        // Native sheets block Radix onOpenChange(false); dismiss via provider like Cancel/X.
        if (isNative) {
          e.preventDefault()
          dismissCtx?.requestClose()
        }
      }}
      {...props}
    />
  )
})
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, onClick, ...props }, ref) => {
  const dismissCtx = useNativeSheetDismissContext()
  const { isNative } = useNativePlatform()
  return (
    <AlertDialogPrimitive.Cancel
      ref={ref}
      className={cn(
        buttonVariants({ variant: "outline" }),
        "mt-2 sm:mt-0",
        className
      )}
      onClick={(e) => {
        onClick?.(e)
        if (isNative) {
          e.preventDefault()
          dismissCtx?.requestClose()
        }
      }}
      {...props}
    />
  )
})
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
